import { type ReactNode } from 'react';
import { tv } from 'tailwind-variants';
import { SegmentTabs, type SegmentTabItem } from './SegmentTabs.js';
import { panelTitle } from './panelHeader.styles.js';

const shell = tv({
  base: 'box-border flex max-w-full min-w-0 flex-col rounded-xl border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] shadow-[var(--elevation-1)]',
  variants: {
    fillHeight: {
      true: 'h-full min-h-0 flex-1',
      false: '',
    },
  },
  defaultVariants: {
    fillHeight: false,
  },
});

/** Primary chrome: title/tabs left, primary CTA right — never buried under search. */
const primaryHeaderRow = tv({
  base: 'flex min-w-0 w-full max-w-full flex-wrap items-start justify-between gap-[var(--space-4)] border-b border-[var(--color-rail-card-border)] px-[var(--space-4)] py-[var(--space-3)] sm:items-center',
});

/**
 * Secondary tools (search, view toggle, settings) — own row so CTA stays pinned.
 * When below primary title chrome: Title→Toolbar uses --space-6 top padding.
 */
/** Spec1-P9 §9.6 — md+ nowrap; compact wrap so search can take full width (FIX 5 Block B). */
const toolsRowClass = tv({
  base: 'flex min-w-0 w-full max-w-full flex-wrap items-center gap-[var(--space-2)] border-b border-[var(--color-rail-card-border)] bg-[color-mix(in_oklab,var(--color-rail-page-bg)_55%,var(--color-an-surface))] px-[var(--space-4)] pb-[var(--space-3)] md:flex-nowrap',
  variants: {
    afterTitle: {
      true: 'pt-[var(--space-6)]',
      false: 'pt-[var(--space-3)]',
    },
  },
  defaultVariants: {
    afterTitle: false,
  },
});

/** Toolbar→Filters: --space-5 top; Filters→Content handled on scroll region when filters present. */
const filterRowClass = tv({
  base: 'border-b border-[var(--color-rail-card-border)] px-[var(--space-4)] pb-[var(--space-3)] pt-[var(--space-5)]',
});

/**
 * Table scroll region (G25) — see `docs/governance/DATA_TABLE_SHELL_CONTRACT.md`.
 * Spec1-P10 §10.3 / Pattern A: `bodyLayout="cards"` drops max-lg h-scroll so
 * compact card surfaces never inherit miniaturized table scroll.
 */
const tableRegion = tv({
  base: 'rp-table-container',
  variants: {
    fillHeight: {
      true: 'min-h-0 flex-1',
      false: '',
    },
    bodyLayout: {
      table: [
        'max-lg:overflow-x-auto',
        'max-lg:[&_table]:min-w-max',
        'max-lg:[&_th:first-child]:sticky max-lg:[&_td:first-child]:sticky',
        'max-lg:[&_th:first-child]:left-0 max-lg:[&_td:first-child]:left-0',
        'max-lg:[&_th:first-child]:z-[calc(var(--z-sticky)+1)] max-lg:[&_td:first-child]:z-[var(--z-sticky)]',
        'max-lg:[&_th:first-child]:bg-[var(--color-an-surface,white)]',
        'max-lg:[&_td:first-child]:bg-[var(--color-an-surface,white)]',
        'max-lg:[&_th:first-child]:shadow-[var(--elevation-1)]',
        'max-lg:[&_td:first-child]:shadow-[var(--elevation-1)]',
      ].join(' '),
      cards: 'overflow-x-hidden',
    },
  },
  defaultVariants: {
    bodyLayout: 'table',
    fillHeight: false,
  },
});

export type DataTableShellBodyLayout = 'table' | 'cards';

export interface DataTableShellProps {
  /**
   * In-card section / table label (`panelTitle` / h2).
   * Must **not** repeat the shell page title from `EnterprisePageHeader`
   * (e.g. `inventory.tableTitle`, never `inventory.pageTitle` / emoji `screenTitle`).
   */
  readonly title?: string;
  readonly tabs?: readonly SegmentTabItem[];
  readonly activeTabId?: string;
  readonly onTabChange?: (id: string) => void;
  /** Accessible name for the status/bucket `SegmentTabs` tablist. */
  readonly tabsAriaLabel?: string;
  readonly toolbarLeft?: ReactNode;
  /**
   * Secondary tools (search, toggles, settings). Rendered on a dedicated row
   * under tabs so primary CTAs in `toolbarRight` stay top-right.
   *
   * **ATPS-13 / D21 (EXC-D21-001):** list search stays here (`toolbarCenter`).
   * Do **not** move search to Spec Part 7 search-left or the page header.
   */
  readonly toolbarCenter?: ReactNode;
  /** Primary actions (Add …) — pinned top-right beside tabs. */
  readonly toolbarRight?: ReactNode;
  readonly filterRow?: ReactNode;
  /**
   * Table body / empty / skeleton content.
   * Ban: `LoadingSpinner` / `ScreenState loading` inside this slot (prefer skeleton rows).
   */
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
  /**
   * Pattern A body mode. Use `cards` when compact/card surface is mounted so the
   * shared scroll region does not enable horizontal table scroll on mobile.
   */
  readonly bodyLayout?: DataTableShellBodyLayout;
  /** Stretch to parent height (command-center widget grid equal-height rows). */
  readonly fillHeight?: boolean;
}

export function DataTableShell({
  title,
  tabs,
  activeTabId,
  onTabChange,
  tabsAriaLabel,
  toolbarLeft,
  toolbarCenter,
  toolbarRight,
  filterRow,
  children,
  footer,
  className,
  bodyLayout = 'table',
  fillHeight = false,
}: DataTableShellProps): JSX.Element {
  const hasTabs = tabs !== undefined && activeTabId !== undefined && onTabChange !== undefined;
  const hasPrimaryHeader =
    title !== undefined ||
    toolbarLeft !== undefined ||
    toolbarRight !== undefined ||
    hasTabs;
  const hasToolsRow = toolbarCenter !== undefined;

  return (
    <section
      className={shell({ fillHeight, className })}
      data-testid="data-table-shell"
      data-body-layout={bodyLayout}
    >
      {hasPrimaryHeader ? (
        <div className={primaryHeaderRow()} data-testid="data-table-shell-primary">
          <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-2)]">
            {title ? <h2 className={panelTitle()}>{title}</h2> : null}
            {toolbarLeft}
            {hasTabs ? (
              <SegmentTabs
                tabs={tabs}
                activeId={activeTabId}
                onChange={onTabChange}
                ariaLabel={tabsAriaLabel}
                variant="analytics"
              />
            ) : null}
          </div>
          {toolbarRight ? (
            <div
              className="flex min-w-0 w-full max-w-full flex-nowrap items-center justify-end gap-[var(--space-2)] md:w-auto md:shrink-0"
              data-testid="data-table-shell-actions"
            >
              {toolbarRight}
            </div>
          ) : null}
        </div>
      ) : null}
      {hasToolsRow ? (
        <div
          className={toolsRowClass({ afterTitle: hasPrimaryHeader })}
          data-testid="data-table-shell-tools"
        >
          {toolbarCenter}
        </div>
      ) : null}
      {filterRow ? <div className={filterRowClass()}>{filterRow}</div> : null}
      <div
        className={tableRegion({
          bodyLayout,
          fillHeight,
          className: (() => {
            if (filterRow) {
              return 'pt-[var(--space-6)]';
            }
            if (hasToolsRow) {
              return 'pt-[var(--space-5)]';
            }
            return undefined;
          })(),
        })}
        data-testid="data-table-scroll"
        data-body-layout={bodyLayout}
      >
        {children}
      </div>
      {footer ? (
        <div className="border-t border-[var(--color-rail-card-border)] px-[var(--space-4)] py-[var(--space-3)]">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
